// API Service for Broker and Review data
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const brokerAPI = {
  // Get all brokers
  async getBrokers(activeOnly = true) {
    try {
      const response = await fetch(`${API_URL}/api/brokers?active_only=${activeOnly}`);
      if (!response.ok) throw new Error('Failed to fetch brokers');
      return await response.json();
    } catch (error) {
      console.error('Error fetching brokers:', error);
      return [];
    }
  },

  // Get single broker by ID
  async getBroker(brokerId) {
    try {
      const response = await fetch(`${API_URL}/api/brokers/${brokerId}`);
      if (!response.ok) throw new Error('Broker not found');
      return await response.json();
    } catch (error) {
      console.error('Error fetching broker:', error);
      return null;
    }
  },

  // Create new broker (admin)
  async createBroker(brokerData) {
    try {
      const response = await fetch(`${API_URL}/api/brokers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brokerData)
      });
      if (!response.ok) throw new Error('Failed to create broker');
      return await response.json();
    } catch (error) {
      console.error('Error creating broker:', error);
      return null;
    }
  },

  // Update broker (admin)
  async updateBroker(brokerId, updateData) {
    try {
      const response = await fetch(`${API_URL}/api/brokers/${brokerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!response.ok) throw new Error('Failed to update broker');
      return await response.json();
    } catch (error) {
      console.error('Error updating broker:', error);
      return null;
    }
  },

  // Delete broker (admin) - permanent delete for inactive brokers
  async deleteBroker(brokerId, permanent = false) {
    try {
      const response = await fetch(`${API_URL}/api/brokers/${brokerId}?permanent=${permanent}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete broker');
      return await response.json();
    } catch (error) {
      console.error('Error deleting broker:', error);
      return null;
    }
  }
};

export const reviewAPI = {
  // Get reviews for a broker
  async getReviews(brokerId = null, approvedOnly = true) {
    try {
      let url = `${API_URL}/api/reviews?approved_only=${approvedOnly}`;
      if (brokerId) {
        url += `&broker_id=${brokerId}`;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch reviews');
      return await response.json();
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }
  },

  // Create new review
  async createReview(reviewData) {
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create review');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },

  // Get pending reviews (admin)
  async getPendingReviews() {
    try {
      const response = await fetch(`${API_URL}/api/reviews/pending`);
      if (!response.ok) throw new Error('Failed to fetch pending reviews');
      return await response.json();
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      return [];
    }
  },

  // Approve review (admin)
  async approveReview(reviewId) {
    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isApproved: true })
      });
      if (!response.ok) throw new Error('Failed to approve review');
      return await response.json();
    } catch (error) {
      console.error('Error approving review:', error);
      return null;
    }
  },

  // Delete review (admin)
  async deleteReview(reviewId) {
    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete review');
      return await response.json();
    } catch (error) {
      console.error('Error deleting review:', error);
      return null;
    }
  },

  // Update review (admin)
  async updateReview(reviewId, updateData) {
    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!response.ok) throw new Error('Failed to update review');
      return await response.json();
    } catch (error) {
      console.error('Error updating review:', error);
      return null;
    }
  },

  // Get all reviews (including approved)
  async getAllReviews() {
    try {
      const response = await fetch(`${API_URL}/api/reviews?approved_only=false`);
      if (!response.ok) throw new Error('Failed to fetch all reviews');
      return await response.json();
    } catch (error) {
      console.error('Error fetching all reviews:', error);
      return [];
    }
  }
};

// Email Subscription API
export const subscriptionAPI = {
  // Subscribe email
  async subscribe(name, email, source = 'trading_guide') {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, source })
      });
      return await response.json();
    } catch (error) {
      console.error('Error subscribing:', error);
      return { success: false, message: 'Network error' };
    }
  },

  // Get all subscriptions (admin)
  async getSubscriptions() {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions`);
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      return await response.json();
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      return [];
    }
  },

  // Delete subscription (admin)
  async deleteSubscription(subscriptionId) {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete subscription');
      return await response.json();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      return null;
    }
  },

  // Export URLs
  getExportCSVUrl() {
    return `${API_URL}/api/subscriptions/export/csv`;
  },

  getExportXLSXUrl() {
    return `${API_URL}/api/subscriptions/export/xlsx`;
  }
};

// First-party Analytics API
import { hasStatisticsConsent } from '@/lib/consent';

export const analyticsAPI = {
  track(type, data = {}) {
    // GDPR/TTDSG: only track after explicit statistics consent
    if (!hasStatisticsConsent()) return;
    // Mirror to Google Analytics 4 (no-op until GA is loaded). Pageviews handled separately.
    if (type !== 'pageview') {
      import('@/lib/ga').then(({ gaEvent }) => gaEvent(type, data)).catch(() => {});
    }
    try {
      const body = JSON.stringify({ type, ...data });
      // Use sendBeacon when available so tracking survives page unload
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/api/analytics/track`, blob);
        return;
      }
      fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* non-blocking */
    }
  },

  async getSummary(days = 30) {
    try {
      const res = await fetch(`${API_URL}/api/analytics/summary?days=${days}`);
      if (!res.ok) throw new Error('Failed to fetch analytics');
      return await res.json();
    } catch (e) {
      console.error('Error fetching analytics:', e);
      return null;
    }
  }
};
